import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiManager } from './api/ApiManager';
import { useApplicationStore } from './applicationStore';
import {READING, TABLE} from "@/constants";
import localForageService from "@/services/localForage";
import imageHandler from "@/utils/imageHandler";
import {StoreManagerRepository} from "@/repositories";
import { createReadingConditionStore } from '@/stores/conditionStore';

export const useReadingStore = defineStore(
  'readingStore',
  () => {
    const applicationStore = useApplicationStore();
    const userDetails = computed(() => applicationStore.userDetails || false);

    const storeCategories = {
      [userDetails.value.id]: {
        reading: {},
        answer: {},
        picture: {},
        element: {}
      }
    };

    const pullCallbackFunction = async (key, images) => {
      if (key === 'picture') {
        await localForageService.removeData(TABLE.QUESTION_PICTURES)
        for (const key in images) {
          const fileOrBlob = await imageHandler.fromBase64(images[key], key);
          // Store as plain Blob to avoid iOS Safari IndexedDB issues with File objects
          const blob = fileOrBlob instanceof File
            ? fileOrBlob.slice(0, fileOrBlob.size, fileOrBlob.type)
            : fileOrBlob;
          await localForageService.storeData(TABLE.QUESTION_PICTURES, key, blob);
        }
      }
    };
    const apiPushOverrideFunction = async (payload) => {
      let response;
      payload.images = await imageHandler.extractImagesFromLocalForage(payload, TABLE.QUESTION_PICTURES);
      response = await StoreManagerRepository.push('readings', payload);
      delete response.data.data.images;
      return response.data.data;
    };

    const options = {
      apiManager: new ApiManager('readings', apiPushOverrideFunction, pullCallbackFunction),
      readonly: false,
      sync: true,
      allTime: false,
      persistentLocalId: false,
      storePushIsChunkedKey: 'reading'
    }
    const data = ref(storeCategories);

    const reading = computed(() => data.value[userDetails.value.id]?.reading || {});
    const answer = computed(() => data.value[userDetails.value.id]?.answer || {});
    const picture = computed(() => data.value[userDetails.value.id]?.picture || {});
    const element = computed(() => data.value[userDetails.value.id]?.element || {});

    function prePushActions() {
      // update reading "is_new" field to true where status is "toBeTransmitted"
      const readings = this.reading;
        Object.entries(readings).forEach(([readingId, reading]) => {
        if (reading.status === READING.STATUS.TO_BE_TRANSMITTED) {
          this.reading[readingId].is_new = true;
          const conditionStore = createReadingConditionStore(readingId);
          this.reading[readingId].question_conditions = {...conditionStore.questions} ?? {};
        }
      })
    }

    function getFromStoreByCategoryAdditionalOperations(storeData, category) {
      if (category === 'reading') {
        return Object.fromEntries(Object.entries(storeData).filter(([key, value]) => value.status !== READING.STATUS.DELETED));
      }
      return storeData;
    }
    const getRawData = () => {
      return data.value?.[userDetails.value?.id] ? JSON.parse(JSON.stringify(data.value?.[userDetails.value?.id])) : {};
    }

    const replaceData = (importedData) => {
      data.value[userDetails.value.id] = importedData;
    }

    return {
      data,
      options,
      reading,
      answer,
      picture,
      element,
      prePushActions,
      getFromStoreByCategoryAdditionalOperations,
      getRawData,
      replaceData
    }
  },
  {
    persistedState: {
      overwrite: true
    }
  }
);